import CustomAutocompleteWithListButtons from "@/components/Form/CustomAutocompleteWithListButtons";
import { useActiveBeeswaxList } from "@/modules/BeeswaxList/beeswaxList.hook";
import { listTypeList } from "@/modules/BeeswaxList/data";
import {
  SubModule,
  SubModuleCommonProps,
} from "@/modules/Presets/TargetingExpression/targetingExpression.interface";
import { formateDataForCustomSelect } from "@/utils/helpers";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Box } from "@mui/material";
import { FieldValues } from "react-hook-form";
const moduleName = "app_site";

// The list type each picker targets, keyed by the Beeswax targeting key it writes.
// componentKey is what listTypeList uses to identify a type for the "+" create dialog.
const listTypeByComponentKey = (componentKey: string) =>
  listTypeList.find((listItem) => listItem.componentKey === componentKey)?.value;

export default function AppSite<
  T extends FieldValues,
  U extends SubModule = "APP_SITE"
>({
  control,
  errors,
  setValue,
  getValues,
  handleCreateCustomList,
  handleEditCustomList,
  parentName,
  domainList,
  domainListLoading,
  dealList,
  dealListLoading,
  appBundleList,
  appBundleListLoading,
  appIdList,
  appIdListLoading,
  isEdit,
}: AppSiteProps<T, U>) {
  const {
    data: localDomainList,
    isLoading: localDomainListLoading,
    isFetching: localDomainListFetching,
  } = useActiveBeeswaxList(
    {
      queryParams: { type: "domain" },
      options: { enabled: !domainList && !domainListLoading },
    },
    "withType"
  );
  const {
    data: localDealList,
    isLoading: localDealListLoading,
    isFetching: localDealListFetching,
  } = useActiveBeeswaxList(
    {
      queryParams: { type: "deal_id" },
      options: { enabled: !dealList && !dealListLoading },
    },
    "withType"
  );
  const {
    data: localAppBundleList,
    isLoading: localAppBundleListLoading,
    isFetching: localAppBundleListFetching,
  } = useActiveBeeswaxList(
    {
      queryParams: { type: "app_bundle" },
      options: { enabled: !appBundleList && !appBundleListLoading },
    },
    "withType"
  );
  const {
    data: localAppIdList,
    isLoading: localAppIdListLoading,
    isFetching: localAppIdListFetching,
  } = useActiveBeeswaxList(
    {
      queryParams: { type: "app_id" },
      options: { enabled: !appIdList && !appIdListLoading },
    },
    "withType"
  );
  domainList = isEdit && domainList ? domainList : localDomainList;
  domainListLoading =
    isEdit && domainListLoading
      ? domainListLoading
      : localDomainListLoading && localDomainListFetching;
  dealList = isEdit && dealList ? dealList : localDealList;
  dealListLoading =
    isEdit && dealListLoading
      ? dealListLoading
      : localDealListLoading && localDealListFetching;
  appBundleList = isEdit && appBundleList ? appBundleList : localAppBundleList;
  // Test the parent's loading flag, not its data: the data variable is reassigned
  // on the line above, and while the parent query is still in flight it is
  // undefined anyway — so keying off it reports "not loading" for the whole fetch
  // and the picker renders selected chips with blank labels against an empty
  // itemList. Same shape as the domain_list and deal_id blocks above.
  appBundleListLoading =
    isEdit && appBundleListLoading
      ? appBundleListLoading
      : localAppBundleListLoading && localAppBundleListFetching;
  appIdList = isEdit && appIdList ? appIdList : localAppIdList;
  appIdListLoading =
    isEdit && appIdListLoading
      ? appIdListLoading
      : localAppIdListLoading && localAppIdListFetching;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "auto",
        padding: 2,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <CustomAutocompleteWithListButtons
          primaryName={`${parentName}.${moduleName}.deal_id_list.value.included`}
          secondaryName={`${parentName}.${moduleName}.deal_id_list.value.excluded`}
          itemList={formateDataForCustomSelect(dealList?.results, "name", "id")}
          handleEdit={handleEditCustomList}
          parentControl={control}
          label={"Deal List"}
          extraLabelButtons={[
            {
              icon: AddRoundedIcon,
              handleOnClick: () =>
                handleCreateCustomList(listTypeByComponentKey("deal")),
              toolTipText: "Add",
            },
          ]}
          loading={dealListLoading}
        />
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <CustomAutocompleteWithListButtons
          primaryName={`${parentName}.${moduleName}.domain_list.value.included`}
          secondaryName={`${parentName}.${moduleName}.domain_list.value.excluded`}
          itemList={formateDataForCustomSelect(
            domainList?.results,
            "name",
            "id"
          )}
          handleEdit={handleEditCustomList}
          parentControl={control}
          label={"Domain List"}
          toolTip={`To target domains, you must first create a "domain list". To create a domain list click on the "+New" button, select the "List" option and then select "Domain List" as the list type. Once your domain list is created it will appear here for you to target.`}
          extraLabelButtons={[
            {
              icon: AddRoundedIcon,
              handleOnClick: () =>
                handleCreateCustomList(listTypeByComponentKey("domain")),
              toolTipText: "Add",
            },
          ]}
          loading={domainListLoading}
        />
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <CustomAutocompleteWithListButtons
          primaryName={`${parentName}.${moduleName}.app_bundle_list.value.included`}
          secondaryName={`${parentName}.${moduleName}.app_bundle_list.value.excluded`}
          itemList={formateDataForCustomSelect(
            appBundleList?.results,
            "name",
            "id"
          )}
          handleEdit={handleEditCustomList}
          parentControl={control}
          label={"App Bundle List"}
          toolTip={`To target app bundles, you must first create an "app bundle list". To create one click the "+" button here, or use "+New" > "List" and select "App Bundle List" as the list type. Once created it will appear here for you to target.`}
          extraLabelButtons={[
            {
              icon: AddRoundedIcon,
              handleOnClick: () =>
                handleCreateCustomList(listTypeByComponentKey("appBundle")),
              toolTipText: "Add",
            },
          ]}
          loading={appBundleListLoading}
        />
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <CustomAutocompleteWithListButtons
          primaryName={`${parentName}.${moduleName}.app_id_list.value.included`}
          secondaryName={`${parentName}.${moduleName}.app_id_list.value.excluded`}
          itemList={formateDataForCustomSelect(
            appIdList?.results,
            "name",
            "id"
          )}
          handleEdit={handleEditCustomList}
          parentControl={control}
          label={"App ID List"}
          toolTip={`To target app IDs, you must first create an "app ID list". To create one click the "+" button here, or use "+New" > "List" and select "App ID List" as the list type. Once created it will appear here for you to target.`}
          extraLabelButtons={[
            {
              icon: AddRoundedIcon,
              handleOnClick: () =>
                handleCreateCustomList(listTypeByComponentKey("appId")),
              toolTipText: "Add",
            },
          ]}
          loading={appIdListLoading}
        />
      </Box>
    </Box>
  );
}

type AppSiteProps<T extends FieldValues, U> = U extends "APP_SITE"
  ? AppSite<T>
  : never;
interface AppSite<T extends FieldValues> extends SubModuleCommonProps<T> {
  domainList: any;
  dealList: any;
  domainListLoading: boolean;
  dealListLoading: boolean;
  appBundleList: any;
  appBundleListLoading: boolean;
  appIdList: any;
  appIdListLoading: boolean;
}
